import { Dispatch, SetStateAction, useContext, useMemo, useCallback } from 'react';

import cn from 'classnames';

import { MdExpandMore, MdExpandLess, MdInfoOutline } from 'react-icons/md';
import { Form, Alert, OverlayTrigger, Tooltip } from 'react-bootstrap';

import { Button } from '../../Components/Button';
import { Checkbox } from '../../Components/FormCheck';
import { Appearance, Intent, Size } from '../../Components/designConstants';

import { useSetError, useSetAreYouSure } from '../../redux/modals';

import { setOrderStatus } from './use-streaming-buying';

import { useExperimentFlag } from '../../utils/experiment-context';

import { OrderViewContext } from './order-view-utils';

import './OrderView.scss';

// Networks whose RTB IOs are actually sent (Send IO / Generate PDF stay visible).
const RTB_SENDABLE_NETWORKS = ['CIVSCI', 'TMOBILE'];

interface OrderControlsProps {
  expanded: boolean;
  setExpanded: Dispatch<SetStateAction<boolean>>;
  isGhostBidding: boolean;
  setIsGhostBidding: Dispatch<SetStateAction<boolean>>;
}

export const OrderControls = ({
  expanded,
  setExpanded,
  isGhostBidding,
  setIsGhostBidding
}: OrderControlsProps) => {
  const setAreYouSure = useSetAreYouSure();
  const setError = useSetError();

  const enableBeeswaxIntegration = useExperimentFlag('enableBeeswaxIntegration');

  const {
    clearOrderChanges,
    flightChangeMap,
    pdfText,
    setPDFText,
    clearPDFText,
    hasChanges,
    setPDFTextToDefault,
    order,
    setSaving,
    onSave,
    sendFlightsToBeeswax,
    refetchOrder,
    generatePDF,
    isNewOrder,
    setShowSendView,
    network
  } = useContext(OrderViewContext);

  const isRTB = useMemo(() => {
    const { flights } = order;
    for (const flight of flights) {
      const combo = { ...flight, ...(flightChangeMap[flight.id] || {}) };
      if (!combo.platform.includes('RTB')) {
        return false;
      }
    }
    return Object.values(flightChangeMap).every(flight =>
      (flight.platform || 'RTB').includes('RTB')
    );
  }, [order, flightChangeMap]);

  const hasFlightsWithDsp = useCallback(
    (dsp: string) => {
      const { flights } = order;
      for (const flight of flights) {
        const combo = { ...flight, ...(flightChangeMap[flight.id] || {}) };
        if (combo.dsp === dsp) {
          return true;
        }
      }
      return Object.values(flightChangeMap).some(flight => (flight.dsp || '') === dsp);
    },
    [order, flightChangeMap]
  );

  const hasBeeswaxFlights = useMemo(() => hasFlightsWithDsp('Beeswax'), [hasFlightsWithDsp]);

  // SAM (Spotify Ads Manager) is a biddable RTB DSP whose IO must still be sent to execute.
  const hasSamFlights = useMemo(() => hasFlightsWithDsp('SAM'), [hasFlightsWithDsp]);

  const forceCommit = useCallback(async () => {
    setSaving(true);
    try {
      if (enableBeeswaxIntegration && hasBeeswaxFlights) {
        await sendFlightsToBeeswax();
      }

      await setOrderStatus(order.id, 20);
      refetchOrder();

      setSaving(false);
    } catch (e) {
      setError({
        message: `Failed to commit order. Error: ${e.message}.`,
        reportError: e
      });
      setSaving(false);
    }
  }, [
    order,
    setError,
    setSaving,
    refetchOrder,
    enableBeeswaxIntegration,
    hasBeeswaxFlights,
    sendFlightsToBeeswax
  ]);

  // RTB orders are dummy IOs that never get sent — except for RTB_SENDABLE_NETWORKS, and except
  // SAM, whose IO still has to reach Spotify to execute.
  const canSendIO = !isRTB || RTB_SENDABLE_NETWORKS.includes(network) || hasSamFlights;

  return (
    <div className='order-controls'>
      <Button
        data-testid='order-controls-expander'
        className={cn('expander', 'w-100', { expanded })}
        onClick={() => setExpanded(prev => !prev)}
      >
        {expanded ? <MdExpandLess /> : <MdExpandMore />}
      </Button>
      {expanded && (
        <div className='order-controls-body'>
          <div className='pdf-text'>
            <div className='label'>
              <span>PDF Text</span>
              <Button appearance={Appearance.EMPTY} onClick={clearPDFText}>
                Discard changes
              </Button>
              <Button appearance={Appearance.EMPTY} onClick={setPDFTextToDefault}>
                Reset to default
              </Button>
            </div>
            <Form.Control
              as='textarea'
              rows={3}
              value={pdfText || ''}
              onChange={e => setPDFText(e.target.value)}
              onBlur={() => setPDFText(text => text.trim())}
            />
          </div>
          {isRTB && (
            <div className='ghost-bidding-options'>
              <Checkbox
                className='ghost-bidding-checkbox'
                checked={isGhostBidding}
                onChange={() => setIsGhostBidding(prev => !prev)}
                disabled={!isNewOrder}
              />{' '}
              This is a ghost bidding IO 👻
              <OverlayTrigger
                placement='right-start'
                overlay={
                  <Tooltip>
                    Checking this will auto-push your flights into a Ghost Bidding specific campaign
                    in Beeswax. You will not be able to change this value once you save your flight.
                  </Tooltip>
                }
              >
                <MdInfoOutline className='ghost-bidding-info' />
              </OverlayTrigger>
            </div>
          )}
          <div className='button-row'>
            <Button
              size={Size.LG}
              design={Intent.ERROR}
              disabled={!hasChanges}
              onClick={() => {
                setAreYouSure({
                  title: 'Discard All Changes?',
                  message:
                    "You're about to discard all your local changes. Are you sure you want to continue?",
                  cancelText: 'Cancel',
                  okayText: 'Continue',
                  onOkay: clearOrderChanges
                });
              }}
            >
              Discard Changes
            </Button>
            <Button
              size={Size.LG}
              data-testid='save-order-button'
              className='save-button'
              disabled={!hasChanges}
              onClick={() => onSave()}
            >
              Save
            </Button>
            {/* Don't allow commit of Beeswax flights until changes are saved
                or we might not yet have derived IDs for new flights. */}
            <Button
              size={Size.LG}
              appearance={Appearance.OUTLINED}
              className='save-button'
              disabled={
                (enableBeeswaxIntegration && hasBeeswaxFlights && hasChanges) ||
                (!hasChanges && order.status === 20)
              }
              onClick={() => {
                if (enableBeeswaxIntegration && hasBeeswaxFlights) {
                  setAreYouSure({
                    title: 'Committing changes to Beeswax',
                    message:
                      "You're about to send these changes to Beeswax.  Are you sure you want to continue?",
                    cancelText: 'Cancel',
                    okayText: 'Continue',
                    onOkay: () => {
                      if (hasChanges) {
                        onSave(true);
                      } else {
                        forceCommit();
                      }
                    }
                  });
                } else if (hasChanges) {
                  onSave(true);
                } else {
                  forceCommit();
                }
              }}
            >
              Commit
            </Button>
            {canSendIO && (
              <Button
                size={Size.LG}
                appearance={Appearance.OUTLINED}
                className='save-button'
                disabled={hasChanges}
                onClick={() => setShowSendView(true)}
              >
                Send
              </Button>
            )}
            {!isNewOrder && canSendIO && (
              <Button
                size={Size.LG}
                design={Intent.SUCCESS}
                className='save-button'
                onClick={() => generatePDF(true)}
                disabled={hasChanges}
              >
                Generate PDF
              </Button>
            )}
            {enableBeeswaxIntegration &&
              hasBeeswaxFlights &&
              !hasChanges &&
              order.status !== 20 && (
                <Alert className='beeswax-sync-alert' variant='warning'>
                  You have made changes to Beeswax flights that have not yet been synced into
                  Beeswax. Please commit your changes to update the DSP.
                </Alert>
              )}
            {/* TODO: some day we'll have a changes view fueled by the audit table */}
          </div>
        </div>
      )}
    </div>
  );
};
